import ComponentErrorBoundary from "components/utils/ComponentErrorBoundary";
import { logError, logInfo } from "components/utils/logger";
import { updatePageOgURL, updatePageTitle } from "components/utils/seo";
import { useApp, useQueryDict } from "hooks/app";
import { useMember } from "hooks/member";
import PropTypes from "prop-types";
import React, { Suspense, lazy, useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import { Outlet } from "react-router-dom";

const Alert = lazy(() => import(/* webpackChunkName: "ui-alert" */ "components/ui/alert"));
const ShareLinkDialog = lazy(() => import(/* webpackChunkName: "ui-share-link-dialog" */ "components/ui/share-link-dialog"));
const CardSendControlCenterDialog = lazy(() =>
  import(/* webpackChunkName: "ui-card-send-control-center-dialog" */ "components/ui/card-send-control-center-dialog")
);
const MemberAlert = lazy(() => import(/* webpackChunkName: "member-alert" */ "components/layout/member-alert"));
const Header = lazy(() => import(/* webpackChunkName: "header", webpackPreload: true */ "components/layout/header"));
const Footer = lazy(() => import(/* webpackChunkName: "footer", webpackPreload: true */ "components/layout/footer"));
const CookieBanner = lazy(() => import(/* webpackChunkName: "bottom-c-banner" */ "components/layout/cookie-banner"));
const CardNavigation = lazy(() => import(/* webpackChunkName: "card-navigation" */ "components/cards/card-navigation"));

const LayoutPage = ({ clean = false }) => {
  const { queryDict, pathname, isLandingPage } = useQueryDict();
  const { adminMemberId } = useMember();
  const { alert, closeAlert, alertOpen } = useApp();

  const withCardNav = pathname.startsWith("/cards") || pathname === "/poply" || pathname === "/";

  logInfo("RENDER: Layout");

  let pathClassName = pathname ? pathname.slice(1).replaceAll("/", "-") : "default";
  if (pathname.indexOf("/cards") >= 0) {
    pathClassName = "cards";
  } else if (pathname.indexOf("/browse") >= 0) {
    pathClassName = "browse";
  } else if (pathname.indexOf("/mycards") >= 0) {
    pathClassName = "mycards";
  }

  useEffect(() => {
    // Don't run first time since the tags are being set by the server
    if (!isLandingPage) {
      try {
        updatePageTitle(pathname);
        updatePageOgURL(pathname);
      } catch (error) {
        logError("Error updating meta tags", error);
      }
    }
  }, [pathname]);

  return (
    <>
      <main className={`main-${pathClassName} ${adminMemberId ? "admin-controls" : ""}`}>
        {!clean && !queryDict.clean && (
          <Suspense fallback={<header />}>
            <Header />
          </Suspense>
        )}
        <Suspense fallback={null}>
          <MemberAlert />
        </Suspense>
        {withCardNav && (
          <Suspense fallback={<div className="SSR-card-navigation" />}>
            <CardNavigation />
          </Suspense>
        )}
        <section className="SSR-contents">
          <Outlet />
        </section>
        {!clean && !queryDict.clean && (
          <Suspense fallback={<footer />}>
            <Footer />
          </Suspense>
        )}
      </main>
      <Suspense fallback={null}>
        <Alert {...alert} onClose={closeAlert} open={alertOpen} />
      </Suspense>
      <Suspense fallback={null}>
        <ShareLinkDialog />
      </Suspense>
      <Suspense fallback={null}>
        <CardSendControlCenterDialog />
      </Suspense>
      <Suspense fallback={null}>
        <CookiesProvider>
          <ComponentErrorBoundary>
            <CookieBanner />
          </ComponentErrorBoundary>
        </CookiesProvider>
      </Suspense>
    </>
  );
};

LayoutPage.propTypes = {
  clean: PropTypes.bool,
};

export default LayoutPage;
